import { useContext, useState, useEffect, MouseEvent } from 'react';
import { JSONSchema7 } from 'json-schema';
import { useFormContext } from 'react-hook-form';
import { InputTextField } from '@abbott-components/core';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { SelectChangeEvent } from '@mui/material/Select';
import ClearIcon from '@mui/icons-material/Clear';
import ControlState from '../../Shared/controlState';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import { SelectOption, SelectSubtypes } from './types';
import SingleDropdown from './subTypeEntry/SingleDropdown';
import RadioButtons from './subTypeEntry/RadioButtons';
import MultiDropdown from './subTypeEntry/MultiDropdown';
import MultiCheckboxes from './subTypeEntry/MultiCheckboxes';
import SingleCheckbox from './subTypeEntry/SingleCheckbox';
import isIE11 from '../../Shared/isIE11';
import { OptionOrientation } from '../../Shared/Enums/optionOrientation';
import { GridSize } from '../../Shared/Enums/gridSize';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);
  const { isReadOnlyView } = state;

  const formContext = useFormContext();
  const fieldValueName = `${control.id}-value`;
  const fieldStatusName = `${control.id}-status`;

  const initialSelectedValue = formContext?.getValues(fieldValueName);
  const fieldState = formContext?.getFieldState(fieldValueName);

  const properties = control.schema?.properties as JSONSchema7Properties;
  const labelSchema = properties['label'] as JSONSchema7;
  const subTypeSchema = properties['subType'] as JSONSchema7;
  const optionsSchema = properties['options'] as JSONSchema7;
  const statusEnabledSchema = properties['statusEnabled'] as JSONSchema7;
  const optionOrientationSchema = properties[
    'optionOrientation'
  ] as JSONSchema7;

  const [inputOptions, setInputOptions] = useState<SelectOption[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [multipleSelected, setMultipleSelected] = useState<string[]>([]);
  const [selectType, setSelectType] = useState<SelectSubtypes>(
    SelectSubtypes.SINGLE_DROPDOWN
  );

  const setFormStatusValue = (value: string) => {
    if (!statusEnabledSchema?.default) return;

    const statusSelected = value
      ? inputOptions.find((option) => option.value === value)?.status
      : '';

    formContext?.setValue(fieldStatusName, statusSelected);
  };

  const handleSelectedOption = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;
    formContext?.setValue(fieldValueName, value as string);
    formContext?.clearErrors(fieldValueName);
    setFormStatusValue(value as string);
    setSelectedValue(value as string);
  };

  const handleMultipleSelection = (
    event: SelectChangeEvent<typeof multipleSelected>
  ) => {
    const {
      target: { value }
    } = event;

    const storedValue = (value as string[]).join('|');
    formContext?.setValue(fieldValueName, storedValue);
    formContext?.clearErrors(fieldValueName);
    setMultipleSelected(value as string[]);
  };

  const saveMultipleSelection = (values: string[]) => {
    const storedValue = (values as string[]).join('|');
    formContext?.setValue(fieldValueName, storedValue);
    setMultipleSelected(values as string[]);
  };

  const saveSingleSelection = (value: string) => {
    formContext?.setValue(fieldValueName, value);
    setFormStatusValue(value);
    setSelectedValue(value);
  };

  const handleDeleteChipSelection = (e: MouseEvent, value: string) => {
    e.preventDefault();
    if (multipleSelected.length === 1) {
      formContext?.setValue(fieldValueName, '');
      setMultipleSelected([]);
    }
    const changedChips = multipleSelected.filter((option) => option !== value);
    formContext?.setValue(fieldValueName, changedChips.join('|'));
    setMultipleSelected(changedChips);
  };

  const handleDeleteSingleDeletion = () => {
    formContext?.setValue(fieldValueName, '');
    setFormStatusValue('');
    setSelectedValue('');
  };

  const isHorizontalFourthOrThird =
    (optionOrientationSchema?.default as OptionOrientation) ==
      OptionOrientation.HORIZONTAL &&
    ((properties['gridSize']?.default as GridSize) == 3 ||(properties['gridSize']?.default as GridSize) == 4);

  const renderDeleteButton = (
    <IconButton
      sx={{ display: selectedValue ? 'visible' : 'none' }}
      onClick={handleDeleteSingleDeletion}
    >
      <ClearIcon fontSize="small" />
    </IconButton>
  );

  const renderHiddenFields = () => (
    <Stack sx={{ display: 'none' }}>
      <InputTextField name={fieldValueName} />
      <InputTextField name={fieldStatusName} />
    </Stack>
  );

  const renderValidationErrors = () =>
    !!fieldState?.error && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fieldState?.error?.message}
      </Typography>
    );

  const renderControl: Record<SelectSubtypes, JSX.Element> = {
    [SelectSubtypes.SINGLE_DROPDOWN]: (
      <SingleDropdown
        label={labelSchema?.default as string}
        selectedValue={selectedValue}
        inputOptions={inputOptions}
        deleteButton={renderDeleteButton}
        handleSelectedOption={handleSelectedOption}
        renderValidationErrors={renderValidationErrors}
      />
    ),
    [SelectSubtypes.RADIO_BUTTONS]: (
      <RadioButtons
        label={labelSchema?.default as string}
        selectedValue={selectedValue}
        selectedOptionOrientation={
          optionOrientationSchema?.default as OptionOrientation
        }
        inputOptions={inputOptions}
        saveSingleSelection={saveSingleSelection}
        renderValidationErrors={renderValidationErrors}
        isHorizontalFourthOrThird={isHorizontalFourthOrThird}
      />
    ),
    [SelectSubtypes.MULTI_DROPDOWN]: (
      <MultiDropdown
        label={labelSchema?.default as string}
        multipleSelected={multipleSelected}
        inputOptions={inputOptions}
        isReadOnlyView={isReadOnlyView}
        handleMultipleSelection={handleMultipleSelection}
        handleDeleteChipSelection={handleDeleteChipSelection}
        renderValidationErrors={renderValidationErrors}
      />
    ),
    [SelectSubtypes.MULTI_CHECKBOXES]: (
      <MultiCheckboxes
        label={labelSchema?.default as string}
        multipleSelected={multipleSelected}
        selectedOptionOrientation={
          optionOrientationSchema?.default as OptionOrientation
        }
        inputOptions={inputOptions}
        saveMultipleSelection={saveMultipleSelection}
        renderValidationErrors={renderValidationErrors}
        isHorizontalFourthOrThird={isHorizontalFourthOrThird}
      />
    ),
    [SelectSubtypes.SINGLE_CHECKBOX]: (
      <SingleCheckbox
        label={labelSchema?.default as string}
        selectedValue={selectedValue}
        inputOption={inputOptions[0]}
        saveSingleSelection={saveSingleSelection}
        renderValidationErrors={renderValidationErrors}
      />
    )
  };

  const getBrowserSubType = (): SelectSubtypes => {
    const initialSubType = subTypeSchema.default as unknown as SelectSubtypes;

    if (isIE11()) {
      switch (initialSubType) {
        case SelectSubtypes.SINGLE_DROPDOWN:
          return SelectSubtypes.RADIO_BUTTONS;
        case SelectSubtypes.MULTI_DROPDOWN:
          return SelectSubtypes.MULTI_CHECKBOXES;
      }
    }

    return initialSubType;
  };

  useEffect(() => {
    if (subTypeSchema.default && optionsSchema.default) {
      setSelectType(getBrowserSubType());
      setInputOptions(optionsSchema.default as unknown as SelectOption[]);
    }
  }, []);

  useEffect(() => {
    if (initialSelectedValue) {
      if (
        selectType === SelectSubtypes.MULTI_DROPDOWN ||
        selectType === SelectSubtypes.MULTI_CHECKBOXES
      ) {
        const initialMultiOptions = initialSelectedValue.split('|');
        setMultipleSelected(initialMultiOptions);
      } else {
        setSelectedValue(initialSelectedValue as string);
      }
    }
  }, [selectType]);

  return (
    <>
      {renderHiddenFields()}
      <FormControl
        fullWidth
        sx={{ marginTop: '16px', marginBottom: '8px' }}
        disabled={isReadOnlyView}
      >
        {renderControl[selectType]}
      </FormControl>
    </>
  );
};
